"use client";

import type { Route } from "next";
import { useRouter } from "next/navigation";
import { useState, useTransition } from "react";
import { motion } from "framer-motion";

import type { ClarificationChip } from "@/lib/api/clarify";
import { MOTION, ms } from "@/lib/design-tokens";
import { cn } from "@/lib/utils";

import { ChipCard } from "./ChipCard";
import { ComparisonShell } from "./ComparisonShell";
import { SearchAgainBar } from "./SearchAgainBar";

interface Props {
  rawQuery: string;
  clarifyingQuestion: string | null;
  chips: ClarificationChip[];
  postcode: string;
}

/**
 * The clarification surface rendered on /compare when the user's
 * query is too vague for an accurate paid search.
 *
 * Layout:
 *   - Centered editorial-style heading carrying the AI-generated
 *     clarifying question (with personality)
 *   - Bento grid: recommended chip spans 2 cols (desktop) as the
 *     "hero" pick; the other 4 sit in a 2x2 grid below. Mobile: all
 *     cards stack 1-col.
 *   - The comparison page's search bar (SearchAgainBar) below the
 *     grid, so the user can type a different / more exact product
 *     (submits a fresh /compare search; clarify re-runs)
 *
 * No paid search runs while this surface is showing. Total cost so
 * far for the user: ~$0.0035 (intent parse + chip generation).
 *
 * Click handling:
 *   When the user picks a chip, we intercept the navigation, swap
 *   the grid for the ComparisonShell skeleton immediately (B-P0-09:
 *   same skeleton the streaming surface renders, so there is no
 *   scene change between this handoff and the live result page),
 *   AND fire router.push so Next.js starts loading the new query.
 *   This means the user sees the eventual result-page surface the
 *   instant they click - we do not depend on Next.js's loading.tsx
 *   firing for same-route searchParam navigations (which it
 *   sometimes elides on a fast machine).
 */
export function ClarificationGrid({
  rawQuery,
  clarifyingQuestion,
  chips,
  postcode,
}: Props) {
  const router = useRouter();
  const [pickedChip, setPickedChip] = useState<ClarificationChip | null>(null);
  const [, startTransition] = useTransition();

  const handlePick = (chip: ClarificationChip) => {
    setPickedChip(chip);
    const href =
      `/compare?q=${encodeURIComponent(chip.product_label)}&postcode=${encodeURIComponent(postcode)}&from_chip=1` as Route;
    startTransition(() => {
      router.push(href);
    });
  };

  // Once a chip is picked, the grid is replaced wholesale by the
  // same ComparisonShell skeleton the streaming surface renders.
  // The navigation continues in the background and Next.js will
  // swap in the live results page (or loading.tsx, which renders
  // the same shell) when ready - no flash, no scene change.
  if (pickedChip) {
    // Synthesise a minimal intent from the chip so HeroSkeleton can
    // render the product name immediately instead of "Searching...".
    const previewIntent = {
      raw_query: pickedChip.product_label,
      product_label: pickedChip.product_label,
      brand: null,
      model_number: null,
      category: "other",
      specs: {},
      constraints: [],
      urgency: "casual",
      confidence: 1.0,
      summary: pickedChip.product_label,
    };
    return <ComparisonShell intent={previewIntent} postcode={postcode} />;
  }

  // Sort chips so the recommended one renders first (it gets the
  // hero slot in the bento grid).
  const sortedChips = [...chips].sort((a, b) => {
    if (a.is_recommended && !b.is_recommended) return -1;
    if (!a.is_recommended && b.is_recommended) return 1;
    return 0;
  });

  return (
    <main className="cmp-surface relative mx-auto min-h-screen w-full max-w-3xl bg-background px-5 py-12 sm:px-8 sm:py-16">

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: ms(MOTION.smooth),
          ease: [0.16, 1, 0.3, 1],
        }}
        className="mb-10 text-center"
      >
        <p className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">
          You searched <span className="text-foreground">{rawQuery}</span>
        </p>
        <h1 className="font-sans text-2xl font-medium leading-tight tracking-tight text-foreground sm:text-3xl md:text-4xl">
          {clarifyingQuestion ?? "Help me narrow this down."}
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
          Pick one to compare prices across Australian retailers.
        </p>
      </motion.div>

      <div
        className={cn(
          "grid gap-3 sm:gap-4",
          // Mobile: 1 column. Desktop: 2 columns. Recommended chip
          // spans 2 cols on desktop (set by ChipCard via `isHero`).
          "grid-cols-1 sm:grid-cols-2",
        )}
      >
        {sortedChips.map((chip, idx) => (
          <ChipCard
            key={chip.product_label}
            chip={chip}
            index={idx}
            isHero={idx === 0 && chip.is_recommended}
            postcode={postcode}
            onPick={handlePick}
          />
        ))}
      </div>

      {/* Escape hatch: the comparison page's own search bar, so the user can
          type a different / more exact product. Reuses SearchAgainBar, which
          submits a fresh /compare search (no from_chip) so clarify re-runs
          for the new query. Same component + styling as the results page. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: ms(MOTION.base), delay: 0.5 }}
      >
        <SearchAgainBar initialQuery="" />
      </motion.div>
    </main>
  );
}
